'use es6';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import tinymce from 'tinymce';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { MultiLevelDropdown } from 'foundations-components/transitional/components/Dropdown';
import { DropdownCaret } from 'foundations-components/transitional/components/AbstractDropdown';
import CopilotDropdownButton from 'ai-components-ui-library/components/buttons/CopilotDropdownButton';
import BrandVoiceMenuOptionLabel from './BrandVoiceMenuOptionLabel';
import { useAtomValue } from '../data/atomUtils';
import { autocompleteActiveRangeAtom } from '../data/autocompleteAtoms';
import { isWhitespace } from '../utils';
import { getEditorTextWithAnnotatedRange, getIsBrandVoiceCommand } from '../hscopilotcollaboration/utils';
import { trackInteraction } from '../utils/usageTracking';
import { NoAuthGatesProvider } from 'ai-components-ui-library/hooks/useIsUngated';
import { NOT_USING_TRACKER } from 'ai-components-ui-library/hooks/useTracker';
import { getCopilotSessionIdFromEditor } from '../hscontentgeneration/utils';
import { COLLAB_MODE_INTERACTIONS_TRACKING } from '../hscopilotcollaboration/constants';
import HighlightedTextFloaty from './HighlightedTextFloaty';
import { useTinyMcePopoverLifecycle } from '../hooks/useTinyMcePopoverLifecycle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OverlayButton = ({
  onClick,
  editor,
  onRemove
}) => {
  const blockEvent = useCallback(evt => {
    // Prevent default to avoid blurring the editor in safari
    // onRemove handler runs if this isn't set
    evt.preventDefault();
  }, []);
  useTinyMcePopoverLifecycle({
    editor,
    onRemove
  });
  return /*#__PURE__*/_jsx(HighlightedTextFloaty, {
    editor: editor,
    placement: "bottom",
    children: /*#__PURE__*/_jsxs(CopilotDropdownButton, {
      onMouseDown: blockEvent,
      onClick: onClick,
      use: "secondary",
      usageTracker: NOT_USING_TRACKER,
      size: "xs",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "TinymcePlugins.hscopilotcollaboration.refine"
      }), /*#__PURE__*/_jsx(DropdownCaret, {})]
    })
  });
};
export function commandToMenuOption(command, editor, closePopover, brandVoiceOptions) {
  const isBrandVoiceCommand = getIsBrandVoiceCommand(command);
  const menuOption = {
    id: command.id,
    label: isBrandVoiceCommand ? /*#__PURE__*/_jsx(BrandVoiceMenuOptionLabel, {
      command: command,
      brandVoiceOptions: brandVoiceOptions,
      editorSettingsAppUrl: editor.settings.appUrl
    }) : I18n.text(command.labelKey),
    onClick: () => {
      command.onClick(editor, command, 'highlight');
      closePopover();
    },
    disabled: command.disabled
  };
  if (command.subMenu) {
    delete menuOption.onClick;
    menuOption.options = command.subMenu.map(subCommand => ({
      id: subCommand.id,
      label: I18n.text(subCommand.labelKey),
      onClick: () => {
        command.onClick(editor, subCommand, 'highlight');
        closePopover();
      }
    }));
  }
  return menuOption;
}
const OverlayContent = ({
  editor,
  commands,
  onClosePopover,
  setEditorContext,
  brandVoiceOptions
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closeOtherInlineTargetPopovers = useCallback(() => editor.fire('hsRemoveInlineTargets'), [editor]);
  const copilotSessionId = getCopilotSessionIdFromEditor(editor);
  const handleTrackInteraction = useCallback(action => {
    const meta = {
      interactionType: 'inline-interaction',
      featureCategoryId: 'content_rewrite',
      sessionId: copilotSessionId
      // Note: inApp, passed via tinyMCE's handleTrackInteraction
    };
    trackInteraction(editor, COLLAB_MODE_INTERACTIONS_TRACKING, action, meta);
  }, [copilotSessionId, editor]);

  // Track selection, every time popover is opened (icon is shown).
  useEffect(() => {
    handleTrackInteraction('text-highlighted');
  }, [handleTrackInteraction]);
  const handleButtonClick = useCallback(() => {
    if (popoverOpen) {
      setPopoverOpen(false);
      handleTrackInteraction('menu-closed');
      return;
    }
    setPopoverOpen(true);
    setEditorContext();
    closeOtherInlineTargetPopovers();
    handleTrackInteraction('menu-opened');
  }, [popoverOpen, setEditorContext, closeOtherInlineTargetPopovers, handleTrackInteraction]);
  useEffect(() => {
    const handleOpenEvent = () => {
      handleButtonClick();
    };
    editor.on('hscontentgeneration:openSelectionPopover', handleOpenEvent);
    return () => {
      editor.off('hscontentgeneration:openSelectionPopover', handleOpenEvent);
    };
  }, [editor, handleButtonClick]);
  const OverlayButtonWithSomeProps = useCallback(() => /*#__PURE__*/_jsx(OverlayButton, {
    editor: editor,
    onRemove: onClosePopover,
    onClick: handleButtonClick
  }), [editor, handleButtonClick, onClosePopover]);
  const dropdownOptions = useMemo(() => {
    return commands.map(command => commandToMenuOption(command, editor, () => setPopoverOpen(false), brandVoiceOptions));
  }, [brandVoiceOptions, commands, editor]);
  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsx(MultiLevelDropdown, {
      Button: OverlayButtonWithSomeProps,
      buttonSize: "xs",
      arrowSize: "none",
      buttonUse: "unstyled",
      open: popoverOpen
      // Hack to prevent auto focus from stealing the RTE focus (give it use=unstyled
      // which triggers "no auto focus" logic, but add the classname to
      // bring use="list" styles back)
      ,
      use: "unstyled",
      options: dropdownOptions
    })
  });
};
const useIsEditorActive = editor => {
  const [isActive, setIsActive] = useState('false');
  const activateHandler = useCallback(() => {
    setIsActive(true);
  }, []);
  const deactivateHandler = useCallback(() => {
    setIsActive(false);
  }, []);
  useEffect(() => {
    editor.on('activate', activateHandler);
    editor.on('deactivate', deactivateHandler);
    return () => {
      editor.off('activate', activateHandler);
      editor.off('deactivate', deactivateHandler);
    };
  }, [editor, activateHandler, deactivateHandler]);
  return {
    isActive
  };
};
const useHandlers = editor => {
  const [selectionText, setSelectionText] = useState('');
  const [editorContext, setEditorContext] = useState('');
  const [isMouseDown, setIsMouseDown] = useState(false);
  const updateEditorContext = useCallback(() => {
    const range = editor.selection.getRng();
    const context = getEditorTextWithAnnotatedRange(editor, range);
    setEditorContext(context);
    // Make sure to reset the range for Safari
    editor.selection.setRng(range);
  }, [editor]);
  const {
    onMouseDown,
    onMouseUp,
    handleSelectionChange
  } = useMemo(() => {
    let isExpectingNodeChangeEvent = false;
    function onMouseDownHandler() {
      isExpectingNodeChangeEvent = true;
      setIsMouseDown(true);
    }
    function selectionChangeHandler() {
      isExpectingNodeChangeEvent = false;
      const text = editor.selection.getContent({
        format: 'text'
      });
      const isCollapsed = editor.selection.isCollapsed();
      if (isWhitespace(text) || isCollapsed) {
        setSelectionText('');
        setEditorContext('');
        return;
      }
      setSelectionText(text);
    }
    function onMouseUpHandler() {
      setIsMouseDown(false);
      if (isExpectingNodeChangeEvent) {
        isExpectingNodeChangeEvent = false;
        selectionChangeHandler();
      }
    }
    return {
      onMouseDown: onMouseDownHandler,
      onMouseUp: onMouseUpHandler,
      handleSelectionChange: selectionChangeHandler
    };
  }, [editor]);
  const resetSelection = useCallback(() => {
    setSelectionText('');
    setEditorContext('');
  }, []);
  useEffect(() => {
    editor.on('NodeChange', handleSelectionChange);
    editor.on('mousedown', onMouseDown);
    // Handle mouseup that can occur both on the child frame and on the parent app
    editor.on('mouseup', onMouseUp);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      editor.off('NodeChange', handleSelectionChange);
      editor.off('mousedown', onMouseDown);
      editor.off('mouseup', onMouseUp);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseDown, onMouseUp, handleSelectionChange, editor]);
  return {
    selectionText,
    editorContext,
    setEditorContext: updateEditorContext,
    resetSelection,
    isUpdatingSelectionByMouse: isMouseDown
  };
};
const HighlightCommandPopoverManager = ({
  commands,
  // handlers already attached to commands
  editor,
  brandVoiceOptions
}) => {
  const {
    selectionText,
    resetSelection,
    setEditorContext,
    isUpdatingSelectionByMouse
  } = useHandlers(editor);
  if (!commands || commands.length === 0) {
    return null;
  }
  if (!selectionText || editor.id !== tinymce.activeEditor.id ||
  // Do not show popover if the user is changing the text selection by mouse
  isUpdatingSelectionByMouse) {
    return null;
  }
  return /*#__PURE__*/_jsx(OverlayContent, {
    editor: editor,
    commands: commands,
    onClosePopover: resetSelection,
    setEditorContext: setEditorContext,
    brandVoiceOptions: brandVoiceOptions
  });
};
const ActiveEditorGuard = ({
  editor,
  brandVoiceOptions,
  commands
}) => {
  var _editor$settings$gate;
  const {
    isActive
  } = useIsEditorActive(editor);
  const autocompleteRange = useAtomValue(autocompleteActiveRangeAtom);
  // TinyMCE menu edge case: Don't show if the TinyMCE Content Assistant menu is open
  if (!isActive || autocompleteRange) {
    return null;
  }
  const gates = (_editor$settings$gate = editor.settings.gates) !== null && _editor$settings$gate !== void 0 ? _editor$settings$gate : [];
  return /*#__PURE__*/_jsx(NoAuthGatesProvider, {
    gates: gates,
    children: /*#__PURE__*/_jsx(HighlightCommandPopoverManager, {
      commands: commands,
      editor: editor,
      brandVoiceOptions: brandVoiceOptions
    })
  });
};
export default ActiveEditorGuard;